import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type WebsiteRole = "admin" | "user" | "viewer";

export interface MonitoredWebsite {
  id: string;
  domain: string;
  label: string | null;
  logo_url: string | null;
  sla_business_days: number;
  sla_warning_days: number;
  role: WebsiteRole;
}

interface SelectedWebsiteContextType {
  websites: MonitoredWebsite[];
  selectedWebsite: MonitoredWebsite | null;
  setSelectedWebsiteId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SelectedWebsiteContext = createContext<SelectedWebsiteContextType>({
  websites: [],
  selectedWebsite: null,
  setSelectedWebsiteId: () => {},
  loading: true,
  refresh: async () => {},
});

export const useSelectedWebsite = () => useContext(SelectedWebsiteContext);

export const SelectedWebsiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [websites, setWebsites] = useState<MonitoredWebsite[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const fetchWebsites = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    // Fetch websites the user is a member of, with their role
    const { data: memberships } = await supabase
      .from("website_members")
      .select("website_id, role")
      .eq("user_id", user.id);

    if (!memberships || memberships.length === 0) {
      setWebsites([]);
      setLoading(false);
      return;
    }

    const websiteIds = memberships.map(m => m.website_id);
    const roleMap: Record<string, WebsiteRole> = {};
    for (const m of memberships) {
      roleMap[m.website_id] = m.role as WebsiteRole;
    }

    const { data } = await supabase
      .from("monitored_websites")
      .select("id, domain, label, logo_url, sla_business_days, sla_warning_days")
      .in("id", websiteIds)
      .order("created_at");

    const sites = (data ?? []).map(w => ({
      ...w,
      role: roleMap[w.id] || "viewer" as WebsiteRole,
    })) as MonitoredWebsite[];

    setWebsites(sites);
    if (sites.length > 0 && !sites.find(s => s.id === selectedId)) {
      setSelectedId(sites[0].id);
    }
    setLoading(false);
  };

  useEffect(() => { fetchWebsites(); }, []);

  const selectedWebsite = websites.find(w => w.id === selectedId) ?? null;

  return (
    <SelectedWebsiteContext.Provider value={{
      websites,
      selectedWebsite,
      setSelectedWebsiteId: setSelectedId,
      loading,
      refresh: fetchWebsites,
    }}>
      {children}
    </SelectedWebsiteContext.Provider>
  );
};
